import React, { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import type { Timesheet } from '@/types/timesheet';

export const REJECTION_MIN_CHARS = 20;
export const REJECTION_MAX_CHARS = 500;

const QUICK_REASONS = [
  'Incorrect project code',
  'Missing entry descriptions',
  "Hours don't match schedule",
  'Non-billable time on billable project',
  'Duplicate entries',
];

interface RejectTimesheetDialogProps {
  /** Timesheet being rejected — null closes the dialog */
  timesheet: Timesheet | null;
  /** Whether the org requires a reason before a rejection can be submitted */
  requiresReason: boolean;
  /** True while the rejection request is in flight */
  isRejecting: boolean;
  onCancel: () => void;
  onConfirm: (reason: string) => void;
}

/**
 * Reject dialog for a single timesheet. The reason text is held locally so that
 * typing never re-renders the approval grid behind the dialog.
 */
export function RejectTimesheetDialog({
  timesheet,
  requiresReason,
  isRejecting,
  onCancel,
  onConfirm,
}: RejectTimesheetDialogProps) {
  const [reason, setReason] = useState('');

  // Reset only when a new timesheet is opened so the text is not cleared
  // mid close-animation.
  useEffect(() => {
    if (timesheet) setReason('');
  }, [timesheet]);

  const trimmedReason = reason.trim();
  const charCount = reason.length;
  const meetsMinimum = trimmedReason.length >= REJECTION_MIN_CHARS;
  const charsRemaining = REJECTION_MIN_CHARS - trimmedReason.length;

  return (
    <Dialog
      open={!!timesheet}
      onOpenChange={() => {
        // Keep the dialog open while the rejection is in flight
        if (isRejecting) return;
        onCancel();
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            Reject Timesheet{timesheet?.employeeName ? ` — ${timesheet.employeeName}` : ''}
          </DialogTitle>
          <DialogDescription>
            Please provide a reason for rejecting this timesheet
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <p className="text-sm text-muted-foreground">
            Your reason will be sent directly to the employee. Please be specific about what needs to be corrected.
          </p>
          <div className="flex flex-wrap gap-1.5">
            {QUICK_REASONS.map((quickReason) => (
              <Badge
                key={quickReason}
                variant="outline"
                className="cursor-pointer hover:bg-accent transition-colors"
                onClick={() => {
                  setReason((current) => {
                    const separator = current.trim() ? '. ' : '';
                    return (current.trim() + separator + quickReason).slice(0, REJECTION_MAX_CHARS);
                  });
                }}
              >
                {quickReason}
              </Badge>
            ))}
          </div>
          <Textarea
            placeholder="Enter rejection reason..."
            value={reason}
            onChange={(e) => setReason(e.target.value.slice(0, REJECTION_MAX_CHARS))}
            rows={4}
          />
          <div className="flex items-center justify-between text-xs">
            <span className={(!requiresReason || meetsMinimum) ? 'text-muted-foreground invisible' : 'text-muted-foreground'}>
              {charCount === 0
                ? `Minimum ${REJECTION_MIN_CHARS} characters required`
                : `${charsRemaining} more character${charsRemaining === 1 ? '' : 's'} needed`}
            </span>
            <span className={cn(
              'font-medium',
              charCount >= REJECTION_MAX_CHARS * 0.9
                ? 'text-destructive'
                : (!requiresReason || meetsMinimum)
                  ? 'text-green-600'
                  : 'text-amber-500'
            )}>
              {charCount} / {REJECTION_MAX_CHARS}
            </span>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" disabled={isRejecting} onClick={onCancel}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={() => onConfirm(trimmedReason)}
            disabled={(requiresReason && !meetsMinimum) || isRejecting}
          >
            {isRejecting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            {isRejecting ? 'Rejecting…' : 'Reject Timesheet'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
